import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { Wrench, AlertTriangle, CheckCircle, Plus, Trash2, Clock, Cog, Package, RotateCcw } from 'lucide-react';
import ProofImageUpload from '../components/ProofImageUpload';
import { HourMeterScanner } from '../components/HourMeterScanner';

export default function ChangeOutPage() {
  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [machineCtx, setMachineCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastCreatedCycleId, setLastCreatedCycleId] = useState(null);

  // New cycle fields
  const [installDate, setInstallDate] = useState(new Date().toISOString().split('T')[0]);
  const [hourMeter, setHourMeter] = useState('');
  const [meterPhoto, setMeterPhoto] = useState('');
  const [meterOcr, setMeterOcr] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [entries, setEntries] = useState([{ product_id: '', supplier_id: '', quantity: 6 }]);

  // Closure fields (only when active cycle exists)
  const [closureReason, setClosureReason] = useState('worn_out');
  const [closureRemarks, setClosureRemarks] = useState('');

  const closureReasons = [
    { value: 'worn_out', label: 'Worn Out' },
    { value: 'broken', label: 'Broken' },
    { value: 'damaged', label: 'Damaged' },
    { value: 'trial_ended', label: 'Trial Ended' },
    { value: 'preventive_replacement', label: 'Preventive Replacement' },
    { value: 'unknown', label: 'Unknown' },
  ];

  useEffect(() => {
    Promise.all([
      api.get('/machines').then(r => setMachines(r.data.filter(m => m.status === 'active'))),
      api.get('/products').then(r => setProducts(r.data.filter(p => p.status === 'active'))),
      api.get('/suppliers').then(r => setSuppliers(r.data)),
    ]).catch(() => toast.error('Failed to load master data'));
  }, []);

  const handleMachineSelect = async (machineId) => {
    setSelectedMachine(machineId);
    setMachineCtx(null);
    if (!machineId) return;
    try {
      setLoading(true);
      const res = await api.get(`/machines/${machineId}/context`);
      setMachineCtx(res.data);
      setHourMeter('');
    } catch {
      toast.error('Failed to load machine context');
    } finally {
      setLoading(false);
    }
  };

  const addEntry = () => setEntries([...entries, { product_id: '', supplier_id: '', quantity: 6 }]);
  const removeEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));
  const updateEntry = (i, field, val) => {
    const updated = [...entries];
    updated[i] = { ...updated[i], [field]: val };
    if (field === 'product_id') {
      const prod = products.find(p => p.product_id === val);
      if (prod?.supplier_id) updated[i].supplier_id = prod.supplier_id;
    }
    setEntries(updated);
  };

  const handleQuickRepeat = () => {
    const lastCycle = machineCtx?.last_closed_cycle;
    if (!lastCycle?.entries?.length) return;
    const repeated = lastCycle.entries.map(e => ({
      product_id: e.product_id || '',
      supplier_id: e.supplier_id || '',
      quantity: e.quantity || 6,
    }));
    setEntries(repeated);
    toast.success('Pre-filled from last cycle configuration');
  };

  const getSupplierName = (supplierId) => {
    if (!supplierId) return null;
    const sup = suppliers.find(s => s.supplier_id === supplierId);
    return sup?.name || null;
  };

  const handleSubmit = async () => {
    if (!selectedMachine) return toast.error('Select a machine');
    if (!installDate) return toast.error('Installation date is required');
    if (!hourMeter || parseFloat(hourMeter) < 0) return toast.error('Valid hour meter reading required');
    if (entries.some(e => !e.product_id)) return toast.error('Select a product for each entry');

    const activeCycle = machineCtx?.active_cycle;
    if (activeCycle) {
      if (parseFloat(hourMeter) < activeCycle.installation_hours) return toast.error(`Hour meter must be >= ${activeCycle.installation_hours} (installation hours of active cycle)`);
    }

    try {
      setSubmitting(true);
      const hrs = parseFloat(hourMeter);
      const payload = {
        machine_id: selectedMachine,
        entries: entries.map(e => ({ product_id: e.product_id, supplier_id: e.supplier_id, quantity: parseInt(e.quantity) || 1, strata: '', remarks: '' })),
        installation_date: installDate,
        installation_hours: hrs,
        closure_hours: activeCycle ? hrs : 0,
        closure_reason: activeCycle ? closureReason : '',
        closure_remarks: closureRemarks,
        operator_remarks: remarks,
        installation_meter_photo: meterPhoto || '',
        installation_ocr_value: meterOcr,
      };
      const res = await api.post('/cycles/change-out', payload);
      toast.success(res.data.message);
      const newCycleId = res.data.new_cycle?.cycle_id;
      setLastCreatedCycleId(newCycleId);
      // Reset form but keep machine context for proof upload
      setHourMeter('');
      setMeterPhoto('');
      setMeterOcr(null);
      setRemarks('');
      setClosureRemarks('');
      setEntries([{ product_id: '', supplier_id: '', quantity: 6 }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit change-out');
    } finally {
      setSubmitting(false);
    }
  };

  const activeCycle = machineCtx?.active_cycle;
  const machineInfo = machineCtx?.machine;

  return (
    <div className="space-y-6" data-testid="change-out-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-heading" data-testid="page-title">New Installation / Change-Out</h1>
        <p className="text-sm text-slate-500 mt-1">Primary workflow: Install new tooth points. If an active cycle exists, it will be automatically closed.</p>
      </div>

      {/* Step 1: Machine Selection */}
      <Card className="p-5">
        <Label className="text-sm font-semibold text-slate-700">Step 1: Select Machine</Label>
        <Select value={selectedMachine} onValueChange={handleMachineSelect}>
          <SelectTrigger className="mt-2 bg-white" data-testid="machine-select">
            <SelectValue placeholder="Select machine by serial number..." />
          </SelectTrigger>
          <SelectContent>
            {machines.map(m => (
              <SelectItem key={m.machine_id} value={m.machine_id}>
                {m.serial_number} — {m.brand} {m.model} ({m.machine_class})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      {loading && <div className="text-center py-8 text-slate-400">Loading machine context...</div>}

      {/* Machine Context Card */}
      {machineInfo && (
        <Card className="p-5 border-slate-200 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-200"><Cog size={18} className="text-slate-600" /></div>
              <div>
                <p className="text-sm font-bold text-slate-900">{machineInfo.brand} {machineInfo.model}</p>
                <p className="text-xs text-slate-500">S/N: {machineInfo.serial_number} | Class: {machineInfo.machine_class}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">{machineCtx.site?.name || 'No site'}</p>
              <p className="text-xs text-slate-400">{machineCtx.company?.name || ''}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Active Cycle Warning */}
      {activeCycle && (
        <Card className="p-5 border-amber-200 bg-amber-50/50" data-testid="active-cycle-card">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-800">Active Cycle Found — Will Be Auto-Closed</p>
              <p className="text-xs text-amber-700 mt-1">Installed on {activeCycle.installation_date} at {activeCycle.installation_hours?.toLocaleString()} hrs</p>
              <div className="mt-2 space-y-1">
                {activeCycle.entries?.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                    <Package size={12} />
                    <span className="font-medium">{e.product_name || e.product_id}</span>
                    <span>x{e.quantity}</span>
                    {e.supplier_name && <span className="text-amber-500">({e.supplier_name})</span>}
                  </div>
                ))}
              </div>

              {/* Closure fields */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-amber-700">Closure Reason</Label>
                  <Select value={closureReason} onValueChange={setClosureReason}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="closure-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>{closureReasons.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-amber-700">Closure Remarks</Label>
                  <Input value={closureRemarks} onChange={e => setClosureRemarks(e.target.value)} placeholder="Optional" className="mt-1 bg-white" />
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2: New Installation Details */}
      {machineInfo && (
        <Card className="p-5" data-testid="new-installation-card">
          <div className="flex items-center gap-2 mb-4">
            <Wrench size={18} className="text-emerald-600" />
            <Label className="text-sm font-semibold text-slate-700">Step 2: New Installation Details</Label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <Label className="text-xs text-slate-500">Installation Date *</Label>
              <Input type="date" value={installDate} onChange={e => setInstallDate(e.target.value)} className="mt-1 bg-white" data-testid="install-date" />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-xs text-slate-500">
                  {activeCycle ? 'Machine Hour Meter at Change-Out *' : 'Machine Hour Meter at Installation *'}
                </Label>
                <HourMeterScanner onDetected={(v, img) => { setHourMeter(String(v)); setMeterPhoto(img); setMeterOcr(v); }} testId="scan-hour-meter" />
              </div>
              <Input type="number" value={hourMeter} onChange={e => setHourMeter(e.target.value)} placeholder={activeCycle ? `>= ${activeCycle.installation_hours}` : 'e.g. 15000'} className="mt-1 bg-white" data-testid="hour-meter" />
              {activeCycle && (
                <div className="mt-1 space-y-0.5">
                  <p className="text-xs text-slate-500">Previous reading: <span className="font-semibold text-slate-700" data-testid="prev-hour-meter">{activeCycle.installation_hours?.toLocaleString()} hrs</span> <span className="text-slate-400">({activeCycle.installation_date})</span></p>
                  <p className="text-xs text-amber-600">This reading will close the previous cycle and start the new one.</p>
                </div>
              )}
            </div>
          </div>

          {/* Tooth Point Entries */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Tooth Point Entries</Label>
              <div className="flex gap-2">
                {machineCtx?.last_closed_cycle?.entries?.length > 0 && (
                  <Button size="sm" variant="outline" onClick={handleQuickRepeat} className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="quick-repeat-btn">
                    <RotateCcw size={14} className="mr-1" /> Quick Repeat
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={addEntry} className="text-xs" data-testid="add-entry-btn">
                  <Plus size={14} className="mr-1" /> Add Product
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {entries.map((entry, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg" data-testid={`entry-row-${i}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                    <div className="sm:col-span-5">
                      <Label className="text-xs text-slate-500">Product *</Label>
                      <Select value={entry.product_id} onValueChange={v => updateEntry(i, 'product_id', v)}>
                        <SelectTrigger className="mt-1 bg-white text-xs" data-testid={`entry-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.brand} - {p.code} ({p.product_type})</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-4">
                      <Label className="text-xs text-slate-500">Supplier</Label>
                      <div className="mt-1 px-3 py-2 bg-slate-100 border border-slate-200 rounded-md text-xs text-slate-600 min-h-[36px] flex items-center" data-testid={`entry-supplier-${i}`}>
                        {getSupplierName(entry.supplier_id) || <span className="text-slate-400 italic">Auto-filled from product</span>}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-slate-500">Qty</Label>
                      <Input type="number" value={entry.quantity} onChange={e => updateEntry(i, 'quantity', e.target.value)} className="mt-1 bg-white text-xs" min={1} />
                    </div>
                    <div className="sm:col-span-1 flex justify-end sm:justify-center">
                      {entries.length > 1 && (
                        <Button size="icon" variant="ghost" onClick={() => removeEntry(i)} className="text-red-400 hover:text-red-600 h-8 w-8">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Operator Remarks</Label>
            <Textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Optional notes" className="mt-1 bg-white" rows={2} />
          </div>
        </Card>
      )}

      {/* Submit */}
      {machineInfo && !lastCreatedCycleId && (
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => { setSelectedMachine(''); setMachineCtx(null); }} data-testid="cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-changeout-btn">
            {submitting ? 'Submitting...' : activeCycle ? 'Close Previous & Install New' : 'Create New Installation'}
          </Button>
        </div>
      )}

      {/* Proof Image Upload (after successful submission) */}
      {lastCreatedCycleId && (
        <Card className="p-5 bg-emerald-50 border-emerald-200" data-testid="proof-upload-card">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={18} className="text-emerald-600" />
            <h3 className="text-sm font-semibold text-emerald-800">Installation Recorded Successfully</h3>
          </div>
          <p className="text-xs text-emerald-700 mb-4">You can now attach proof images of the tooth point condition. This step is optional.</p>
          <ProofImageUpload
            cycleId={lastCreatedCycleId}
            machineId={selectedMachine}
            siteId={machineCtx?.site?.site_id}
            companyId={machineCtx?.company?.company_id}
          />
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => { setLastCreatedCycleId(null); setSelectedMachine(''); setMachineCtx(null); }} className="text-xs" data-testid="done-btn">
              Done — Start New Entry
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
